import {
  Component,
  ComponentMirror,
  ComponentRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  reflectComponentType,
  SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PlatformWidgetComponent } from './platform-widget';
import { CartridgeWidget, PlatformWidget, Widget, WidgetIdentity } from '../../models/dashboard.models';
import { cleanUrl } from '@markant-platform/shared/util-helpers';
import { PlatformCartridgeWidgetComponent } from './platform-cartridge-widget/platform-cartridge-widget.component';
import { VariantsStore } from '../../stores/variants/variants.store';

@Component({
  selector: 'markant-platform-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
})
export class WidgetComponent implements OnInit, OnChanges {
  readonly store = inject(VariantsStore);
  @Input() dragActive = false;
  @Input() widget!: Widget;
  variantId?: string = '';
  isDemo = false;

  @Output() widgetRemoved: EventEmitter<WidgetIdentity> = new EventEmitter<WidgetIdentity>();

  @ViewChild('placeholder', { read: ViewContainerRef, static: true })
  viewContainer!: ViewContainerRef;

  component?: ComponentRef<PlatformWidgetComponent | PlatformCartridgeWidgetComponent>;

  constructor(
    private router: Router,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    const componentType = (this.widget as PlatformWidget).component as Type<PlatformWidgetComponent>;
    if (this.widget.widgetType === 'PLATFORM' || this.widget.widgetType === 'PLATFORM_CARTRIDGE') {
      this.component = this.viewContainer.createComponent(componentType);
    }

    const component = this.component;

    if (component) {
      const inputNames = (reflectComponentType(componentType) as ComponentMirror<PlatformWidgetComponent>).inputs.map(
        input => input.propName
      );
      Object.keys(this.widget).forEach(attribute => {
        if (inputNames.includes(attribute)) {
          component.setInput(attribute, this.widget[attribute as keyof Widget]);
        }
      });
    }
  }

  getWidgetCartridgeVariant(): void {
    if (this.widget?.id) {
      const bookedVariants = this.store.bookedVariants();
      if (bookedVariants) {
        const variant = bookedVariants.find(variant => variant.cartridgeVariantId === this.widget.cartridgeId);
        this.variantId = variant ? variant.cartridgeVariantId : undefined;
        this.isDemo = variant?.requestType === 'DEMO_REQUEST';
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dragActive']) {
      this.component?.setInput('dragActive', this.dragActive);
    }
    if (this.store.isFetched()) {
      this.getWidgetCartridgeVariant();
    }
  }

  async openLink(): Promise<void> {
    if (this.dragActive) {
      return;
    }

    if (this.widget as PlatformWidget) {
      const { routerLink } = this.widget as PlatformWidget;

      if (routerLink && !this.dragActive) {
        await this.router.navigate([routerLink]);
      }
    }

    const cartridgeWidget = this.widget as CartridgeWidget;
    if (cartridgeWidget.componentName || cartridgeWidget.elementName) {
      if (cartridgeWidget.focusView?.routePath) {
        const focusViewUrl = '/focus-view/' + cleanUrl(cartridgeWidget.focusView.routePath);
        await this.router.navigate([focusViewUrl]);
      } else if (cartridgeWidget.externalLink) {
        window.open(cartridgeWidget.externalLink, '_blank', 'noopener');
      }
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  getDisplayName(): string {
    if (!this.widget.displayName) {
      return '';
    }

    return !(this.widget as PlatformWidget).allowedRoles
      ? this.widget.displayName
      : this.translocoService.translate(this.widget.displayName);
  }

  removeWidget({ id, cartridgeId }: Widget): void {
    const widget: WidgetIdentity = { id, cartridgeId };
    this.widgetRemoved.emit(widget);
  }
}
